'use client'

import React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useDispatch } from 'react-redux'
import clsx from 'clsx'
import DOMPurify from 'isomorphic-dompurify'
import { clearAllData } from '@/store/roleModelFinderSlice'
import type { AppDispatch } from '@/store'
import AiIcon from '@/components/Shared/Icon/AiIcon'
import styles from './styles.module.scss'

interface RoleModelFinderBannerProps {
  from: 'top' | 'company' | 'story'
  companyId?: number
  className?: string
}

type RoleModelFinderBannerText = Record<
  'top' | 'company' | 'story',
  {
    title: string
    description: string
    ctaText: string
  }
>

const RoleModelFinderBanner: React.FC<RoleModelFinderBannerProps> = ({
  from = 'top',
  companyId,
  className,
}) => {
  const router = useRouter()
  const dispatch = useDispatch<AppDispatch>()

  const bannerTextClass = 'bannerBlock'

  const text: RoleModelFinderBannerText = {
    top: {
      title: `AIが発見する、<span class="${bannerTextClass}">あなただけの"目標の先輩"。</span>`,
      description: `キャリアの解像度を<span class="${bannerTextClass}">グッと上げよう</span>`,
      ctaText: '診断をはじめる',
    },
    company: {
      title: `この会社の、<span class="${bannerTextClass}">ロールモデルを探そう。</span>`,
      description: `AIがあなたを分析し、<span class="${bannerTextClass}">最適なロールモデルを診断。</span>`,
      ctaText: '診断をはじめる',
    },
    story: {
      title: `あなたに合う先輩が、<span class="${bannerTextClass}">他にもきっといる。</span>`,
      description: `AIがあなたを分析し、<span class="${bannerTextClass}">最適なロールモデルを診断。</span>`,
      ctaText: 'この会社のロールモデルをいますぐ診断',
    },
  }

  const { title, description, ctaText } = text[from]

  const params = new URLSearchParams()
  if (companyId) {
    params.set('company_ids', String(companyId))
  }
  if (from === 'company' || from === 'story') {
    params.set('disable_dropdown', 'true')
  }
  const linkHref = `/role-model-finder${params.size > 0 ? `?${params.toString()}` : ''}`

  const handleClick = async (): Promise<void> => {
    await dispatch(clearAllData())
    router.push(linkHref)
  }

  const inner = (
    <div className={styles.roleModelFinderBannerInner}>
      <div className={styles.roleModelFinderBannerContent}>
        <div className={styles.roleModelFinderBannerName}>
          {from === 'top' && <AiIcon />}
          <span className={styles.roleModelFinderBannerNameText}>ロールモデル診断</span>
        </div>
        <h4
          className={styles.roleModelFinderBannerTitle}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }}
        />
        <p
          className={styles.roleModelFinderBannerDescription}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
        />
      </div>
      {from !== 'top' && (
        <button type='button' onClick={handleClick} className={styles.roleModelFinderBannerCta}>
          {ctaText}
        </button>
      )}
    </div>
  )

  if (from === 'top') {
    return (
      <Link
        href={linkHref}
        onClick={() => dispatch(clearAllData())}
        className={clsx(styles.roleModelFinderBanner, styles[from], className)}
      >
        {inner}
      </Link>
    )
  }

  return <div className={clsx(styles.roleModelFinderBanner, styles[from], className)}>{inner}</div>
}

export default RoleModelFinderBanner
